import {
  AfterContentInit,
  Component,
  computed,
  ElementRef,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
  inject,
} from '@angular/core';
import { Swiper } from 'swiper/types';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TransactionsApiService } from '../../../../../_services/transactions-service/transactions-api.service';
import { TransactionsService } from '../_service/transactions.service';
import {
  SplitCategorySelectionObject,
  Transaction,
  TransactionAccount,
  TransactionCategory,
  TransactionItem,
  TransactionItemError,
  TransactionItemFromAPI,
  TransactionReceiptFile,
  TransactionStatusLabel,
  TransactionUpdateRequest,
  VatType,
} from '../../../../../_interfaces/transactions.interface';
import { OffcanvasService } from '../../../../../_services/offcanvas-service/offcanvas.service';
import { ToastService } from '../../../../../_services/toast-service/toast.service';
import { CurrencyPipe, DatePipe, NgClass, NgOptimizedImage } from '@angular/common';
import { LoaderComponent } from '../../../../../shared/components/loader/loader.component';
import { InfoBlockComponent } from '../../../../../shared/components/info-card-block/info-block.component';
import { map, Observable, of } from 'rxjs';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { OffcanvasCancelBtnComponent } from '../../../../../shared/components/offcanvas/offcanvas-cancel-btn/offcanvas-cancel-btn.component';
import { OffcanvasBaseComponent } from '../../../../../shared/components/offcanvas/offcanvas-base/offcanvas-base.component';
import { NgbCollapse, NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { SelectTransactionCategoryComponent } from './select-transaction-category/select-transaction-category.component';
import { AddCategoryModalComponent } from './add-category-modal/add-category-modal.component';
import { TransactionSplitCategoriesEditComponent } from './transaction-split-categories-edit/transaction-split-categories-edit.component';
import { ConfirmationRemoveModalComponent } from '../../../../../shared/components/modals/confirmation-remove-modal/confirmation-remove-modal.component';
import { TransactionSplitCategoriesViewComponent } from './transaction-split-categories-view/transaction-split-categories-view.component';
import { ReceiptPreviewComponent } from './receipt-preview/receipt-preview.component';
import {
  StatusBadgeComponent,
  statusBadgeDepartments,
} from '../../../../../shared/components/status-badge/status-badge.component';
import {
  AccountingConnectionStatus,
  AccountingContact,
  AccountingProviderCompany,
} from '../../../../../_interfaces/accounting.interface';
import { AccountingService } from '../../_services/accounting/accounting.service';
import { Store } from '@ngxs/store';
import { ToggleLabelSwitchComponent } from '../../../../../shared/components/forms/toggle-label-switch/toggle-label-switch.component';
import { ToggleSwitchComponent } from '../../../../../shared/components/forms/toggle-switch/toggle-switch.component';
import { translateProviderError, translateSyncAuthError } from '../_helpers/transactions-helpers';
import { defaultVatType, vatOptions } from '../_utils/transactions-utils';
import { SubscriptionSelectors } from '../../../../../store/subscription/subscription.selectors';
import { toSignal } from '@angular/core/rxjs-interop';
import { EmptyIndicatorPipe } from '../../../../../shared/pipes/empty-indicator.pipe';
import { AccountingContactsSelectComponent } from '../../../../../shared/components/forms/accounting-contacts-select/accounting-contacts-select.component';
import { TrackingCategoriesComponent } from '../_components/tracking-categories/tracking-categories.component';
import { TaxRateSelectComponent } from '../_components/tax-rate-select/tax-rate-select.component';
import { isEqual } from 'lodash-es';
import { AppFormComponent } from '../../../../../shared/classes/app-form';
import { Method } from '@bo/ng-api';
import { BoFormsModule } from '@bo/ng-forms';
import { FieldComponent } from '../../../../../shared/components/forms/form-field/form-field.component';
import { SelectFieldComponent } from '../../../../../shared/components/forms/select-field/select-field.component';
import { SubmitComponent } from '../../../../../shared/components/forms/submit/submit.component';
import { HttpErrorResponse } from '@angular/common/http';
import { maskConfig } from '../../../../../shared/configs/configs';
import { ReceiptUploadComponent } from '../_components/receipt-upload/receipt-upload.component';
import { ReceiptUploadedTypeDisplayComponent } from '../_components/receipt-uploaded-type-display/receipt-uploaded-type-display.component';
import { FileUploadedTypeDisplayComponent } from '../../../../../shared/components/file-uploaded-type-display/file-uploaded-type-display.component';
import { ReceiptCarousel } from './receipt-carousel/receipt-carousel';

@Component({
  selector: 'app-transactions-edit',
  imports: [
    LoaderComponent,
    InfoBlockComponent,
    DatePipe,
    CurrencyPipe,
    NgSelectModule,
    FormsModule,
    OffcanvasCancelBtnComponent,
    OffcanvasBaseComponent,
    NgOptimizedImage,
    NgbTooltip,
    SelectTransactionCategoryComponent,
    TransactionSplitCategoriesEditComponent,
    NgClass,
    NgbCollapse,
    TransactionSplitCategoriesViewComponent,
    StatusBadgeComponent,
    RouterLink,
    ToggleLabelSwitchComponent,
    ToggleSwitchComponent,
    EmptyIndicatorPipe,
    AccountingContactsSelectComponent,
    TrackingCategoriesComponent,
    TaxRateSelectComponent,
    BoFormsModule,
    FieldComponent,
    SelectFieldComponent,
    SubmitComponent,
    ReceiptUploadComponent,
    ReceiptUploadedTypeDisplayComponent,
    FileUploadedTypeDisplayComponent,
    ReceiptCarousel,
    ReceiptPreviewComponent,
  ],
  templateUrl: './transactions-edit.component.html',
  styleUrl: '../../../../../../static/styles/components/transactions-edit.component.scss',
})
export class TransactionsEditComponent extends AppFormComponent<TransactionUpdateRequest> implements AfterContentInit {
  readonly route = inject(ActivatedRoute);
  readonly toastService = inject(ToastService);
  readonly store = inject(Store);
  readonly transactionsApiService = inject(TransactionsApiService);
  readonly transactionsService = inject(TransactionsService);
  readonly accountingService = inject(AccountingService);
  readonly offcanvasService = inject(OffcanvasService);
  readonly modalService = inject(NgbModal);

  protected readonly fields: string[] = ['description', 'invoice_number', 'vat'];

  protected readonly maskConfig = maskConfig;
  protected readonly Number = Number;

  readonly hasAccounting$ = this.store.select(SubscriptionSelectors.hasAccounting);
  protected readonly hasAccounting = toSignal(this.hasAccounting$, { initialValue: false });

  protected hasActiveConnection: Signal<boolean> = computed(() => {
    return this.accountingService.connectionStatus() === AccountingConnectionStatus.Complete;
  });

  @ViewChild('transactionDetails') transactionDetails!: NgbCollapse;
  @ViewChild('transactionReceipts') transactionReceipts!: NgbCollapse;
  @ViewChild('toggleDetailsBtnContainer') toggleDetailsBtnContainer!: ElementRef;
  @ViewChild('toggleReceiptsBtnContainer') toggleReceiptsBtnContainer!: ElementRef;
  @ViewChild(ToggleLabelSwitchComponent) toggleSwitch!: ToggleLabelSwitchComponent;
  @ViewChild(ToggleSwitchComponent) flagSwitch!: ToggleSwitchComponent;
  @ViewChild(ReceiptCarousel) receiptCarousel?: ReceiptCarousel;

  protected returnUrl: string = '/accounting/transactions';
  transactionDetail = signal<Transaction>({} as Transaction);
  readonly transactionId: string;
  splitCategories: SplitCategorySelectionObject[] = [];
  selectedContact!: AccountingContact;
  syncAuthError = false;
  transactionProviderError: string | null | undefined;
  savedSplitItems: TransactionItemFromAPI[] = [];
  isTransactionDetailsCollapsed = false;
  isTransactionReceiptsCollapsed = false;
  showReceipt = true;
  carouselSwiper?: Swiper; // Store the swiper instance from carousel
  activeSlideIndex = 0; // Track the current active slide

  originalCategory: number | undefined;
  originalTrackingCategory: number[] | undefined;
  originalTax: number | undefined;
  originalVAT: string | undefined;
  originalInvoice: string | undefined;
  originalDescription: string | undefined;
  originalContact!: AccountingContact;
  originalFlag: boolean = false;

  selectedCategoryHasChanged: boolean = false;
  selectedTrackingCatsHasChanged: boolean = false;
  selectedTaxTypeHasChanged: boolean = false;
  selectedVATTypeHasChanged: boolean = false;
  fieldHasChanged: boolean = false;

  selectedCategoryId: number | undefined;
  selectedTrackingCats: number[] = [];
  selectedTaxRateId: number | undefined;
  selectedCategoryHasAccount: boolean = true;
  latestTransactionSaved: boolean = false;
  showContactError: string = '';
  showCatError: string = '';
  showRecError: string = '';
  showItemsError: TransactionItemError[] = []; // Array of error objects from API
  showSingleItemError: string = '';
  nonFieldErrors?: string[] = [];

  protected readonly statusBadgeDepartments = statusBadgeDepartments;
  protected readonly AccountingProviderCompany = AccountingProviderCompany;
  protected readonly Boolean = Boolean;
  protected readonly vatOptions = of(vatOptions);
  protected vatType: number = defaultVatType;

  isLoading: WritableSignal<boolean> = signal(true);
  showCatSyncError: WritableSignal<boolean> = signal<boolean>(false);
  splitTransaction = signal<boolean>(false);
  splitEditMode = signal<boolean>(false); // True when editing existing split items
  splitItems: WritableSignal<TransactionItem[]> = signal([]);
  contactSyncClicked: WritableSignal<boolean> = signal(false);
  isFlagged: WritableSignal<boolean> = signal(false);
  switchToReceiptFileUpload: WritableSignal<boolean> = signal(false);
  categoryPreferredTaxRate: WritableSignal<number | undefined> = signal(undefined);
  receiptToPreview: WritableSignal<TransactionReceiptFile> = signal({} as TransactionReceiptFile);

  readonly provider = computed(() => this.accountingService.provider());

  readonly remainingTotal = computed(() => {
    // Get the total transaction amount and parse it to a number
    const transactionAmount = Number.parseFloat(this.transactionDetail().amount?.toString() ?? '0') || 0;

    // Calculate the sum of all split items entered so far
    const splitItemsTotal = this.splitItems().reduce((sum, item) => {
      const itemAmount = item.amount ? Number.parseFloat(item.amount) : 0;
      return sum + itemAmount;
    }, 0);

    // Return the remaining amount (transaction total - split items total)
    return Number.parseFloat((transactionAmount - splitItemsTotal).toFixed(2));
  });

  trackingCategories = this.accountingService.trackingCategories;

  // Convert saved tracking categories (TransactionTrackingCategory[]) to IDs for component input
  initialSavedTrackingCatIds = computed(() => {
    return this.transactionDetail().tracking?.map((t) => t.id) ?? [];
  });

  isNonEditableStatus = computed(() => {
    const status = this.transactionDetail().status;
    return (
      status === TransactionStatusLabel.SYNCHED ||
      status === TransactionStatusLabel.READY ||
      status === TransactionStatusLabel.ERROR
    );
  });

  statusNew = computed(() => this.transactionDetail().status === TransactionStatusLabel.NEW);
  statusReady = computed(() => this.transactionDetail().status === TransactionStatusLabel.READY);
  statusError = computed(() => this.transactionDetail().status === TransactionStatusLabel.ERROR);
  statusSynched = computed(() => this.transactionDetail().status === TransactionStatusLabel.SYNCHED);

  receiptFileCount = computed(() => this.transactionDetail().receipt_files?.length ?? 0);

  constructor() {
    super();
    const route = this.route;

    this.transactionId = route.snapshot.params['id'];
  }

  ngAfterContentInit(): void {
    this.transactionsService.fetchCategories();
  }

  getInitialData() {
    return this.transactionsApiService.getTransactionById(
      this.transactionId,
    ) as unknown as Observable<TransactionUpdateRequest>;
  }

  handleData(data: Transaction) {
    if (data?.error) {
      this.syncAuthError = translateSyncAuthError(data.error);
      const provider = this.provider();
      if (provider) {
        this.transactionProviderError = translateProviderError(provider, data.error) ?? null;
      }
    }

    // these values are used to check if the user has changed the value
    this.originalCategory = data?.category?.id;
    this.originalVAT = data?.vat?.toString();
    this.originalTax = data?.tax?.id;
    this.originalDescription = data?.description;
    this.originalInvoice = data?.invoice_number;
    this.originalContact = data?.contact;
    this.originalFlag = data?.flag;
    this.originalTrackingCategory = data?.tracking?.map((t) => t.id);

    // these values are used to populate the manual fields of the form and can be re-assigned
    this.transactionDetail.set(data);
    this.selectedCategoryId = data?.category?.id;
    this.selectedTaxRateId = data?.tax?.id;
    this.selectedTrackingCats = data?.tracking?.map((t) => t.id) ?? [];
    this.selectedCategoryHasAccount = Boolean(data.category?.account);
    this.selectedContact = data?.contact ?? { id: null, name: '' };
    this.isFlagged.set(data?.flag ?? false);

    // Check if the transaction has split items
    if (data?.items && data.items.length > 0) {
      this.savedSplitItems = data.items;

      // set split items to be the same as saved items
      this.setSplitItemsFromSaved(data.items);

      // Don't automatically enable splitTransaction - wait for the user to click Edit
      this.splitTransaction.set(false);
      this.splitEditMode.set(false);
    } else {
      // check for category account issues
      this.showCatSyncError.set(!this.statusSynched() && !data?.category?.account && !this.splitItems().length);
    }
  }

  reloadData() {
    this.getInitialData().subscribe({
      next: (data) => {
        this.handleData(data as unknown as Transaction);
      },
    });
  }

  override createForm(): void {
    const options$ = this.transactionsApiService
      .getOptions(this.transactionsApiService.URL_TRANSACTIONS + '/' + this.transactionId + '/', Method.PUT)
      .pipe(
        // Manually assign validator because on BE, DRF just does not put it in OPTIONS.
        map((fields) =>
          fields.map((f) =>
            // temp remove 'unit_price', 'unit_quantity'
            f.name && ['invoice_number'].includes(f.name) ? Object.assign(f, { allowBlank: true }) : f,
          ),
        ),
      );
    const data$ = this.getInitialData();
    this.subscriptions.add(
      this.buildFromOptions(options$, data$).subscribe({
        next: () => {
          data$.subscribe({
            next: (res) => {
              const data = res as unknown as Transaction;

              this.form.get('vat')?.setValue(data.vat?.toString()); // convert back to string to populate

              this.form.get('vat')?.valueChanges.subscribe((value) => {
                this.fieldHasChanged = value !== data.vat?.toString();
                this.latestTransactionSaved = false;
              });

              this.form.get('description')?.valueChanges.subscribe((value) => {
                this.fieldHasChanged = value !== data.description;
                this.latestTransactionSaved = false;
              });

              this.form.get('invoice_number')?.valueChanges.subscribe((value) => {
                this.fieldHasChanged = value !== data.invoice_number;
                this.latestTransactionSaved = false;
              });

              /* temp comment out until design is finalised
              if (this.splitItems().length) {
                this.form.get('unit_price')?.disable();
                this.form.get('unit_quantity')?.disable();
              } else {
                this.form.get('unit_quantity')?.valueChanges.subscribe((value) => {
                  const quantity = Number(value);
                  this.fieldHasChanged = quantity !== Number(data.unit_quantity);
                  if (!quantity) return;
                  this.form.get('unit_price')?.setValue((Number(data.amount) / quantity).toFixed(2), {
                    emitEvent: false,
                  });
                  this.latestTransactionSaved = false;
                });

                this.form.get('unit_price')?.valueChanges.subscribe((value) => {
                  const price = Number(value);
                  this.fieldHasChanged = price !== Number(data.unit_price);
                  if (!price) return;
                  this.form.get('unit_quantity')?.setValue(Number((Number(data.amount) / price).toFixed(2)), {
                    emitEvent: false,
                  });
                  this.latestTransactionSaved = false;
                });
              }
              */

              this.handleData(data);

              this.isLoading.set(false);
            },
          });
        },
      }),
    );
  }

  onUnitPriceFocus(): void {
    const quantity = Number(this.form.get('unit_quantity')?.value);
    const unitPriceControl = this.form.get('unit_price');
    // Only prefill when quantity is 1 and the user hasn't entered a price yet
    if (quantity === 1 && unitPriceControl && !unitPriceControl.value) {
      this.fieldHasChanged = true;
      unitPriceControl.setValue(this.transactionDetail().amount, { emitEvent: false });
    }
  }

  onContactChange($event: AccountingContact) {
    // clear errors on the new selection first
    if (this.selectedContact !== $event) this.showContactError = '';
    this.fieldHasChanged = $event !== this.originalContact;
    this.selectedContact = $event;
    this.latestTransactionSaved = false;
  }

  contactBusySyncing($event: boolean) {
    this.contactSyncClicked.set($event);
  }

  setSplitItemsFromSaved(items: TransactionItemFromAPI[]) {
    this.splitItems.set(
      items.map((item) => {
        const transactionItem: TransactionItem = {
          category: item.category.id,
          // unit_quantity: item.unit_quantity,
          // unit_price: item.unit_price,
          name: item.name,
          amount: item.amount,
          tax: item.tax?.id,
          tracking: item.tracking?.map((t) => t.id) ?? [],
        };
        // Only include vat property if it has a value (not null or undefined)
        if (item.vat != null) {
          transactionItem.vat = item.vat;
        }
        return transactionItem;
      }),
    );
  }

  onOpenTransactionDetails() {
    this.transactionDetails.toggle();
  }

  onOpenTransactionReceipts() {
    this.transactionReceipts.toggle();
  }

  onCategoryChange(event: { categoryId?: number; account?: TransactionAccount; isVisible: boolean }) {
    // clear errors on the new selection first
    if (this.selectedCategoryId !== event.categoryId) this.showCatError = '';

    // set new values
    this.selectedCategoryHasChanged = event.categoryId !== this.originalCategory;
    this.selectedCategoryId = event.categoryId;
    this.selectedCategoryHasAccount = Boolean(event.account);
    this.latestTransactionSaved = false;
    this.categoryPreferredTaxRate.set(event.account?.tax_id);
  }

  onCategorySyncError(hasError: boolean) {
    this.showCatSyncError.set(hasError);
  }

  updateFlag(value: boolean) {
    this.latestTransactionSaved = false;
    let data: TransactionUpdateRequest = { flag: value };

    data = this.addCurrentStateOfSplitItems(data);

    this.transactionsApiService.updateTransaction(this.transactionId, data).subscribe({
      next: () => {
        this.isFlagged.set(value);
      },
      error: () => {
        if (this.flagSwitch) {
          this.flagSwitch.updateValue.set(false);
        }
        this.toastService.show('Something went wrong. Could not update flag status.', 'iconoir-xmark');
      },
    });
  }

  addCurrentStateOfSplitItems(data: Partial<TransactionUpdateRequest>) {
    // Context: Because split items ALWAYS needs to be in the payload, flagging a transaction forces the current open split items to be sent in the payload
    // Therefore if the user is in "edit split mode" we have the send the original split items (before edit) in the payload, otherwise they get saved with the flag.
    // If the user decides to cancel the edit, the original data must remain.
    const convertSavedSplitItems = this.savedSplitItems.map((item) => {
      const transactionItem: TransactionItem = {
        category: item.category.id,
        name: item.name,
        amount: item.amount,
        tax: item.tax?.id,
        tracking: item.tracking?.map((t) => t.id) ?? [],
      };
      // Only include vat property if it has a value (not null or undefined)
      if (item.vat != null) {
        transactionItem.vat = item.vat;
      }
      return transactionItem;
    });

    //this ALWAYS needs to be sent in payload, otherwise split gets wiped.
    if (this.splitItems().length) {
      if (this.splitEditMode()) {
        // send original items
        data.items = this.cleanSplitItems(convertSavedSplitItems);
      } else {
        // send existing items
        data.items = this.cleanSplitItems(this.splitItems());
      }
    }
    return data;
  }

  onSplitItemsChange(items: TransactionItem[]) {
    this.splitItems.set(items);
    // Clear form-level server errors when split items change
    this.clearFormServerErrors();
  }

  onCategoryDeleted(categoryValue: number) {
    // Remove category from the splitCategories array (immutably)
    this.splitCategories = this.splitCategories.filter((cat) => cat.value !== categoryValue);
  }

  onClearAllErrors() {
    this.showItemsError = [];
    // Also clear form-level server errors
    this.clearFormServerErrors();
  }

  /**
   * Clear form-level and field-level server errors.
   * This is needed when split items change because the base FormComponent
   * attaches API errors to the form, but split items aren't form fields.
   */
  private clearFormServerErrors() {
    if (!this.form) return;
    // Clear form-level server errors
    const formErrors = this.form.errors;
    if (formErrors?.['server']) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { server, ...remainingErrors } = formErrors;
      this.form.setErrors(Object.keys(remainingErrors).length > 0 ? remainingErrors : null);
    }

    // Clear field-level server errors
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control?.errors?.['server']) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { server, ...remainingErrors } = control.errors;
        control.setErrors(Object.keys(remainingErrors).length > 0 ? remainingErrors : null);
      }
    });
  }

  // Enter edit mode for existing split items
  onEditSplitItems() {
    // Extract unique categories from saved items
    const categoriesMap = new Map<number, SplitCategorySelectionObject>();

    this.savedSplitItems.forEach((item) => {
      // API returns item.category as a full TransactionCategory object
      const categoryId = item.category.id;
      if (!categoriesMap.has(categoryId)) {
        categoriesMap.set(categoryId, {
          name: item.category.name,
          value: categoryId,
          account_code: item.category.account?.code,
          tax_id: item.category.account?.tax_id,
        });
      }
    });

    // Convert map to array
    this.splitCategories = Array.from(categoriesMap.values());

    // Enable split transaction mode
    this.splitTransaction.set(true);
    this.splitEditMode.set(true);

    // Close toggle boxes
    this.transactionDetails.toggle(false);
    this.transactionReceipts.toggle(false);
  }

  onReceiptDeleted(event: boolean) {
    if (event) {
      this.reloadData();
    }
  }

  onBatchUploadComplete(event: {
    successCount: number;
    failureCount: number;
    failures: Array<{ fileName: string; error: string }>;
  }): void {
    const { successCount, failureCount } = event;

    // Show aggregated success toast
    if (successCount > 0) {
      const message =
        successCount === 1 ? 'Receipt uploaded successfully' : `${successCount} receipts uploaded successfully`;
      this.toastService.show(message, 'iconoir-check-circle');

      // Reload data to show new receipts
      this.reloadData();
    }

    // Show individual failure toasts
    if (failureCount > 0) {
      this.toastService.show(`Failed to upload ${failureCount} receipts`, 'iconoir-xmark');
    }
  }

  onViewReceipt() {
    this.showReceipt = true;
  }

  onViewReceiptFile($event: TransactionReceiptFile) {
    // Find the index of the clicked receipt
    const index = this.transactionDetail().receipt_files?.indexOf($event) ?? 0;

    // Set the active slide index BEFORE opening the carousel
    this.activeSlideIndex = index;

    // Open the carousel (or keep it open)
    this.showReceipt = true;
    this.receiptToPreview.set($event);

    // If carousel is already open and swiper is ready, navigate to the slide
    if (this.carouselSwiper) {
      this.carouselSwiper.slideTo(index);
    }
  }

  /**
   * Called when the carousel swiper is ready
   * Store the swiper instance so we can pass it to thumbnail components
   */
  onCarouselSwiperReady(swiper: Swiper) {
    this.carouselSwiper = swiper;

    // Navigate to the active slide if it's not already there
    // This handles the case when carousel opens from a closed state
    if (swiper.activeIndex !== this.activeSlideIndex && this.activeSlideIndex >= 0) {
      setTimeout(() => {
        swiper.slideTo(this.activeSlideIndex, 0); // 0 = no animation
      }, 0);
    }
  }

  /**
   * Called when the carousel slide changes
   * Update the active slide index for thumbnail highlighting
   */
  onCarouselSlideChanged(index: number) {
    this.activeSlideIndex = index;
  }

  onCloseReceiptCarouselPreview(event: boolean) {
    if (event) {
      this.showReceipt = false;
      this.activeSlideIndex = -1;
    }
  }

  uploadNewReceipt() {
    const modalRef = this.modalService.open(ConfirmationRemoveModalComponent, {
      centered: true,
      container: 'body', // Mount at body level to avoid aria-hidden conflicts with offcanvas
    });

    modalRef.componentInstance.action = 'delete';
    modalRef.componentInstance.subject = 'this receipt';
    modalRef.componentInstance.confirmationBtnText = `Yes, I'm sure`;
    modalRef.componentInstance.removeMessage = `This action can not be undone`;

    modalRef.closed.subscribe({
      next: (confirmed: boolean) => {
        if (confirmed) {
          let data: TransactionUpdateRequest = { receipt: '' };
          data = this.addCurrentStateOfSplitItems(data);
          this.transactionsApiService.updateTransaction(this.transactionId, data).subscribe({
            next: () => {
              this.switchToReceiptFileUpload.set(true);
            },
            error: () => {
              this.switchToReceiptFileUpload.set(false);
              this.toastService.show('Something went wrong. Could not delete receipt.', 'iconoir-xmark');
            },
          });
        }
      },
    });
  }

  addSplitCategory() {
    const modalRef = this.modalService.open(AddCategoryModalComponent, {
      centered: true,
      container: 'body', // Mount at body level to avoid aria-hidden conflicts with offcanvas
    });

    modalRef.closed.subscribe({
      next: (result: { categoryId?: number; account?: TransactionAccount }) => {
        if (!result?.categoryId) return;

        const duplicateCategory = this.splitCategories.find((cat) => cat.value === result.categoryId);
        if (duplicateCategory) {
          this.toastService.show(`Category "${duplicateCategory.name}" has already been added to split categories`);
          return;
        }

        // Look up the category name from the service
        const categoryOptions = this.transactionsService.categoryOptions();
        const selectedCategory = categoryOptions.find((cat: TransactionCategory) => cat.id === result.categoryId);

        if (selectedCategory) {
          // Add the category to the splitCategories array
          // Create a NEW array to trigger change detection
          this.splitCategories = [
            ...this.splitCategories,
            {
              name: selectedCategory.name,
              value: result.categoryId,
              account_code: result.account?.code,
              tax_id: result.account?.tax_id,
            },
          ];
        }
      },
    });
  }

  onSplitTransaction() {
    this.splitTransaction.set(!this.splitTransaction());
    this.transactionDetails.toggle(false);
    this.transactionReceipts.toggle(false);
    this.splitEditMode.set(true);
    this.addSplitCategory();
  }

  onCancelSplitTransaction() {
    if (this.splitCategories.length && !this.savedSplitItems.length) {
      const modalRef = this.modalService.open(ConfirmationRemoveModalComponent, {
        centered: true,
        container: 'body', // Mount at body level to avoid aria-hidden conflicts with offcanvas
      });

      modalRef.componentInstance.action = 'cancel';
      modalRef.componentInstance.subject = 'itemised transactions';
      modalRef.componentInstance.confirmationBtnText = `Yes, I'm sure`;
      modalRef.componentInstance.removeMessage = `By cancelling, all split transaction data will be lost.`;

      modalRef.closed.subscribe({
        next: (confirmed: boolean) => {
          if (confirmed) {
            // Remove category from the items store
            this.splitCategories = [];
            this.splitItems.set([]);
            this.splitTransaction.set(false);
            this.splitEditMode.set(false);
            this.transactionDetails.toggle(true);
          }
        },
      });
    } else {
      this.splitItems.set([]);
      this.splitTransaction.set(false);
      this.splitEditMode.set(false);
      this.transactionDetails.toggle(true);
    }
  }

  onCancelEdit() {
    this.splitTransaction.set(false);
    this.splitEditMode.set(false);
    this.setSplitItemsFromSaved(this.savedSplitItems);
  }

  markReadyButtonDisabled() {
    return Boolean(
      this.showUnsavedChanges() ||
      (!this.splitItems().length && !this.selectedCategoryId) ||
      this.showCatSyncError() ||
      (!this.hasActiveConnection() && !this.statusReady()) ||
      Boolean(!this.selectedContact?.name && !this.statusReady()) ||
      Boolean(!this.splitItems().length && !this.selectedTaxRateId) ||
      this.isFlagged(),
    );
  }

  showContactErrorOnReady() {
    return Boolean(!this.selectedContact?.name && this.statusReady());
  }

  markTransactionReady(transactionId: string) {
    this.subscriptions.add(
      this.transactionsApiService.markTransactionReady(transactionId).subscribe({
        next: () => {
          this.updateTransactionStatus(TransactionStatusLabel.READY);
        },
        error: (error) => {
          // Revert the toggle to false (NEW state) using updateValue model
          if (this.toggleSwitch) {
            this.toggleSwitch.updateValue.set(false);
          }

          this.toastService.show(
            `Could not mark transaction as ready: ${error.error?.non_field_errors}`,
            'iconoir-xmark',
          );
        },
      }),
    );
  }

  unmarkTransactionReady(transactionId: string) {
    if (this.statusError() && this.savedSplitItems.length) {
      this.onEditSplitItems();
    }
    this.latestTransactionSaved = false;
    this.subscriptions.add(
      this.transactionsApiService.unmarkTransactionReady(transactionId).subscribe({
        next: () => {
          this.updateTransactionStatus(TransactionStatusLabel.NEW);
        },
        error: (error) => {
          // Revert the toggle to true (READY state) using updateValue model
          if (this.toggleSwitch) {
            this.toggleSwitch.updateValue.set(true);
          }
          this.toastService.show(`An error occurred: ${error.error?.non_field_errors}`, 'iconoir-xmark');
        },
      }),
    );
  }

  private updateTransactionStatus(newStatus: TransactionStatusLabel): void {
    this.transactionDetail.update((current) => {
      if (!current) return current;
      return { ...current, status: newStatus };
    });
  }

  onToggleTransactionReady(): void {
    const transaction = this.transactionDetail();
    if (transaction) {
      this.markTransactionReady(transaction.id);
    }
  }

  onToggleTransactionUnready(): void {
    const transaction = this.transactionDetail();
    if (transaction) {
      this.unmarkTransactionReady(transaction.id);
    }
  }

  showUnsavedChanges() {
    return (
      this.selectedCategoryHasChanged ||
      this.selectedTrackingCatsHasChanged ||
      this.selectedTaxTypeHasChanged ||
      this.selectedVATTypeHasChanged ||
      this.fieldHasChanged
    );
  }

  splitSaveDisabled() {
    return this.splitItems() && this.remainingTotal() !== 0;
  }

  regularSaveDisabled() {
    return !this.selectedCategoryId || this.form.invalid;
  }

  getTrackingCats(event: number[]) {
    this.selectedTrackingCatsHasChanged = !isEqual(event, this.originalTrackingCategory);
    this.latestTransactionSaved = false;
    this.selectedTrackingCats = event;
  }

  onTaxRateChange(event: number | undefined) {
    this.selectedTaxTypeHasChanged = event !== this.originalTax;
    this.selectedTaxRateId = event;
    this.latestTransactionSaved = false;
  }

  // Clean split items: remove null/undefined unit_price and unit_quantity
  private cleanSplitItems(items: TransactionItem[]): TransactionItem[] {
    return items.map((item) => {
      const cleaned: TransactionItem = { ...item };
      // Remove unit_price if null or undefined
      if (cleaned.unit_price == null) {
        delete cleaned.unit_price;
      }
      // Remove unit_quantity if null or undefined
      if (cleaned.unit_quantity == null) {
        delete cleaned.unit_quantity;
      }
      return cleaned;
    });
  }

  override submitData(formData: TransactionUpdateRequest) {
    // Clear all previous error states before submission
    this.showSingleItemError = '';
    this.showItemsError = [];
    this.showContactError = '';
    this.showCatError = '';
    this.showRecError = '';

    const data: TransactionUpdateRequest = formData;

    if (this.splitItems().length) {
      //this ALWAYS needs to be sent in payload, otherwise it gets wiped.
      data.items = this.cleanSplitItems(this.splitItems());
      // reset any existing non-split related errors before split transaction enabled.
      this.showCatSyncError.set(false);
      delete data.unit_price;
      delete data.unit_quantity;
    } else {
      data.category = this.selectedCategoryId;
      data.tracking = this.selectedTrackingCats;
      data.tax = this.selectedTaxRateId;
      data.vat = (Number(data.vat) as VatType) ?? defaultVatType;
    }

    if (this.hasAccounting()) {
      if (this.selectedContact.name) {
        data.contact = this.selectedContact;
      } else {
        data.contact = null;
      }
    }

    return this.transactionsApiService.updateTransaction(
      this.transactionId,
      data,
    ) as unknown as Observable<TransactionUpdateRequest>;
  }

  handleSubmitSuccess(): void {
    // Clear all error states on successful submission
    this.showContactError = '';
    this.showCatError = '';
    this.showRecError = '';
    this.showItemsError = [];
    this.showSingleItemError = '';
    this.nonFieldErrors = [];

    if (this.splitEditMode()) {
      this.splitEditMode.set(false);
      this.splitTransaction.set(false);
    } else {
      // reset change detectors for manual entry
      this.selectedCategoryHasChanged = false;
      this.selectedVATTypeHasChanged = false;
      this.selectedTaxTypeHasChanged = false;
      this.selectedTrackingCatsHasChanged = false;
    }

    // reset change detectors for manual and split entry
    this.fieldHasChanged = false;
    this.latestTransactionSaved = true;
    this.toastService.show(`You have successfully saved your transaction details`, 'iconoir-check-circle');
    this.reloadData();
    if (this.contactSyncClicked()) {
      this.toastService.show(
        `Wait for contact syncing to complete syncing before closing this window`,
        'iconoir-info-circle',
      );
    } else if (!this.splitItems().length) {
      this.offcanvasService.close(this.returnUrl);
    }
  }

  override handleSubmitError(error: HttpErrorResponse): void {
    super.handleSubmitError(error);
    this.showContactError = error.error?.contact?.name ? error.error.contact.name : '';
    this.showCatError = error.error?.category ? error.error.category[0] : '';
    this.showRecError = error.error?.receipt ? error.error.receipt[0] : '';
    this.showItemsError = error.error?.items ? error.error.items : [];
    if (error.error?.items?.length) {
      if (error.error?.items[0] && typeof error.error?.items[0] !== 'object') {
        this.showSingleItemError = error.error.items[0];
      }
    }
    this.nonFieldErrors = error.error?.non_field_errors;

    // bring the contact field into view when there is a contact error
    if (this.splitTransaction() && this.showContactError) {
      this.transactionDetails.toggle(true);
      this.scrollToTop();
    }
  }

  scrollToTop(): void {
    // Find the LAST child element within the scroll container
    const container = this.toggleDetailsBtnContainer.nativeElement as HTMLElement;
    const firstElement = container.firstElementChild;

    if (firstElement) {
      // Use scrollIntoView on the last element with smooth behaviour
      firstElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start', // Scroll to align the element with the end of the viewport
      });
    } else {
      // Fallback for empty container: scroll container to bottom (if necessary)
      container.scrollTop = container.scrollHeight;
    }
  }
}
